import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PropertyListFacadeService } from './list-view/data/property-list-facade.service';
import { FiltersFacadeService } from './list-view/data/filters-facade.service';
import { CoreDataFacade } from '@letly/state';
import {
  IconService,
  LoggerService,
  NATIVE_AUTH_CALLBACK_URL,
  NativeAuthService,
  PushDeepLinkService,
  PushNotificationService,
} from '@letly/services';
import { PwaInstallService } from './core/services/pwa-install.service';
import { AppUpdateService } from './core/services/app-update.service';
import { NavbarComponent } from './ui-common/navbar/navbar.component';
import { AuthService, GenericError } from '@auth0/auth0-angular';
import { LandlordListingFacade } from './landlord/landlord-listing/data/landlord-listing-facade';
import {
  distinctUntilChanged,
  filter,
  pairwise,
  take,
} from 'rxjs';
import { BottomNavbarComponent } from './ui-common/bottom-navbar/bottom-navbar.component';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PosthogService,
  scrubPiiBeforeSend,
} from './core/services/posthog.service';
import {
  ConsentDecision,
  ConsentService,
} from './core/services/consent.service';
import { ConsentSyncService } from './core/services/consent-sync.service';
import { CONSENT_UI_MODE } from './core/services/consent.config';
import { ConsentBannerComponent } from './core/consent/consent-banner.component';
import { AppUpdateNoticeComponent } from './core/app-update/app-update-notice.component';
import { ConsentDialogComponent } from './core/consent/consent-dialog.component';
import { UtmAttributionService } from './core/services/utm-attribution.service';
import { RoleSwitchNotificationService } from './core/services/role-switch-notification.service';
import { SignOutService } from './core/services/sign-out.service';
import { environment } from '../env/environment';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapacitorApp } from '@capacitor/app';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    BottomNavbarComponent,
    NavbarComponent,
    ConsentBannerComponent,
    AppUpdateNoticeComponent,
  ],
  templateUrl: './app.component.html',
  standalone: true,
  styleUrl: './app.component.scss',
  providers: [PropertyListFacadeService, FiltersFacadeService],
})
export class AppComponent implements OnInit {
  private iconService = inject(IconService);
  private coreDataFacade = inject(CoreDataFacade);
  private authService = inject(AuthService);
  private nativeAuth = inject(NativeAuthService);
  private landlordListingFacade = inject(LandlordListingFacade);
  protected appUpdate = inject(AppUpdateService);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);
  private pwaInstall = inject(PwaInstallService);
  private loggerService = inject(LoggerService);
  private router = inject(Router);
  private posthogService = inject(PosthogService);
  protected readonly consentService = inject(ConsentService);
  protected readonly consentSyncService = inject(ConsentSyncService);
  protected readonly consentUiMode = CONSENT_UI_MODE;
  private signOutService = inject(SignOutService);
  private utmAttribution = inject(UtmAttributionService);
  private readonly roleSwitchNotification = inject(
    RoleSwitchNotificationService,
  );
  private readonly pushNotifications = inject(PushNotificationService);
  private readonly pushDeepLink = inject(PushDeepLinkService);

  private static readonly LOGIN_QUERY_PARAM = 'login';

  private currentUser: { sub?: string; email?: string; name?: string } | null =
    null;
  private previousAnalyticsDecision: ConsentDecision | null = null;

  private readonly analyticsConsentEffect = effect(() => {
    const decision = this.consentService.analytics();
    const fresh =
      this.previousAnalyticsDecision !== null &&
      this.previousAnalyticsDecision !== decision &&
      decision === 'accepted';
    this.previousAnalyticsDecision = decision;
    this.posthogService.applyAnalyticsDecision(decision, fresh);
    if (fresh && this.currentUser) {
      this.identifyUser(this.currentUser);
    }
  });

  private readonly attributionConsentEffect = effect(() => {
    if (this.consentService.attribution() === 'accepted') {
      this.utmAttribution.persistSession();
    } else {
      this.utmAttribution.clearStored();
    }
  });

  private consentDialogOpen = false;
  private pwaPromptScheduled = false;

  private readonly pwaPromptEffect = effect(() => {
    if (this.pwaPromptScheduled || this.consentService.shouldPrompt()) return;
    this.pwaPromptScheduled = true;
    setTimeout(() => this.pwaInstall.promptIfEligible(), 2_000);
  });

  private readonly pushPromptSuppressionEffect = effect(() => {
    this.pushNotifications.setPromptSuppressed(
      this.consentService.shouldPrompt(),
    );
  });

  private readonly consentPromptEffect = effect(() => {
    if (
      this.consentUiMode === 'blocking' &&
      this.consentService.shouldPrompt()
    ) {
      setTimeout(() => this.openConsentDialog());
    }
  });

  private openConsentDialog(): void {
    if (this.consentDialogOpen || !this.consentService.shouldPrompt()) return;
    this.consentDialogOpen = true;
    this.messageService
      .showDialogOrBottomSheet(ConsentDialogComponent, {
        disableClose: true,
        autoFocus: false,
        backdropClass: 'letly-consent-backdrop',
        dialog: { width: '480px' },
      })
      .pipe(take(1))
      .subscribe(() => {
        this.consentDialogOpen = false;
        if (this.consentService.shouldPrompt()) {
          setTimeout(() => this.openConsentDialog());
        }
      });
  }

  ngOnInit(): void {
    this.watchForceLoginQueryParam();
    this.posthogService.init(environment.posthog?.token ?? '', {
      api_host: environment.posthog?.host ?? 'https://eu.i.posthog.com',
      ui_host: 'https://eu.posthog.com',
      capture_exceptions: true,
      capture_pageview: false,
      cookieless_mode: 'on_reject',
      before_send: (event) =>
        this.posthogService.hasObjectedToAggregate()
          ? null
          : scrubPiiBeforeSend(event),
      defaults: '2026-01-30',
    });
    this.posthogService.applyAnalyticsDecision(this.consentService.analytics());
    this.utmAttribution.capture();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.posthogService.posthog.capture('$pageview');
      });

    this.authService.appState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appState) => {
        if (appState?.target) {
          this.router.navigateByUrl(appState.target);
        }
      });

    this.iconService.loadGeneralIcons();
    this.coreDataFacade.loadAmenities();
    this.appUpdate.start();
    this.pushDeepLink.listen();
    this.interceptExternalLinks();
    void this.configureStatusBar();
    this.listenForNativeAuthCallback();

    this.authService.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.landlordListingFacade.loadProperties();

        void this.pushNotifications.initialize();
      });

    this.authService.user$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.sub === curr?.sub),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        this.currentUser = user ?? null;
        if (user) {
          this.identifyUser(user);
        }
      });

    this.authService.isAuthenticated$
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev && !curr),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.signOutService.clearUserScopedState();
      });

    this.authService.error$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((error) => {
        const auth0Error = error as GenericError;
        if (
          auth0Error.error === 'missing_refresh_token' ||
          auth0Error.error === 'invalid_grant'
        ) {
          this.loggerService.error(
            `Got Auth0 error '${auth0Error.error}': ${auth0Error.error_description}`,
          );
          this.signOutService.signOut();
        }
      });
  }

  private watchForceLoginQueryParam(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const tree = this.router.parseUrl(event.urlAfterRedirects);
        if (tree.queryParams[AppComponent.LOGIN_QUERY_PARAM] !== 'true') {
          return;
        }

        delete tree.queryParams[AppComponent.LOGIN_QUERY_PARAM];
        const targetUrl = this.router.serializeUrl(tree);

        this.authService.isAuthenticated$
          .pipe(take(1))
          .subscribe((isAuthenticated) => {
            if (isAuthenticated) {
              this.router.navigateByUrl(targetUrl, { replaceUrl: true });
              return;
            }
            this.nativeAuth.loginWithRedirect({
              appState: { target: targetUrl },
            });
          });
      });
  }

  private listenForNativeAuthCallback(): void {
    if (!Capacitor.isNativePlatform()) return;

    CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      if (!url.startsWith(NATIVE_AUTH_CALLBACK_URL)) return;

      void Browser.close();

      const params = new URL(url).searchParams;

      if (params.has('code')) {
        this.authService
          .handleRedirectCallback(url)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            error: (error) =>
              this.handleNativeAuthFailure(
                `Native auth callback failed: ${JSON.stringify(error)}`,
              ),
          });
        return;
      }

      const authError = params.get('error');
      if (authError) {
        this.handleNativeAuthFailure(
          `Native auth callback error '${authError}': ${params.get('error_description') ?? ''}`,
        );
      }
    });
  }

  private handleNativeAuthFailure(context: string): void {
    this.loggerService.error(context);
    this.messageService.showSnackbar(
      $localize`:@@app.native-auth.error:Sign-in failed. Please try again.`,
      ToastSeverity.Error,
    );
    this.signOutService.signOut();
  }

  private async configureStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    await StatusBar.setOverlaysWebView({ overlay: false }).catch((error) =>
      this.loggerService.error(
        `Failed to disable status bar overlay: ${JSON.stringify(error)}`,
      ),
    );
    await StatusBar.setStyle({ style: Style.Light }).catch((error) =>
      this.loggerService.error(
        `Failed to set status bar style: ${JSON.stringify(error)}`,
      ),
    );
  }

  private interceptExternalLinks(): void {
    if (!Capacitor.isNativePlatform()) return;

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest('a[target="_blank"]');
      if (!(anchor instanceof HTMLAnchorElement) || !anchor.href) return;

      if (anchor.protocol !== 'http:' && anchor.protocol !== 'https:') return;

      if (anchor.origin === window.location.origin) return;

      event.preventDefault();
      void Browser.open({ url: anchor.href });
    });
  }

  private identifyUser(user: {
    sub?: string;
    email?: string;
    name?: string;
  }): void {
    if (this.consentService.analytics() !== 'accepted') {
      return;
    }
    const attributionAllowed = this.consentService.attribution() === 'accepted';
    const firstTouch = attributionAllowed
      ? this.utmAttribution.getFirstTouch()
      : null;
    const lastTouch = attributionAllowed
      ? this.utmAttribution.getLastTouch()
      : null;
    this.posthogService.posthog.identify(
      user.sub,
      {
        email: user.email,
        name: user.name,
        last_utm_source: lastTouch?.params['utm_source'],
        last_utm_medium: lastTouch?.params['utm_medium'],
        last_utm_campaign: lastTouch?.params['utm_campaign'],
        last_utm_term: lastTouch?.params['utm_term'],
        last_utm_content: lastTouch?.params['utm_content'],
      },
      {
        first_utm_source: firstTouch?.params['utm_source'],
        first_utm_medium: firstTouch?.params['utm_medium'],
        first_utm_campaign: firstTouch?.params['utm_campaign'],
        first_utm_term: firstTouch?.params['utm_term'],
        first_utm_content: firstTouch?.params['utm_content'],
        first_landing_path: firstTouch?.landingPath,
        first_referrer: firstTouch?.referrer,
      },
    );
  }
}
